import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { io } from 'socket.io-client';
import { API_BASE } from '../config';
import { playNotificationSound, playAnswerDing, startThinkingSound, stopThinkingSound } from '../utils/sound';
import { unlockAudio } from '../utils/audioUnlock';
import { speakNative, stopNativeTTS } from '../utils/nativeTts';
import { playAudioBuffer } from '../utils/webAudioPlayer';
import { stopAllAudio } from '../utils/audioManager';
import { useI18n } from './I18nContext';

const AppContext = createContext(null);

export const useApp = () => useContext(AppContext);

const SUPER_ADMINS = [
  'drd@synaptics-solutions.com'
];

const VOICE_BY_LANG = {
  fr: 'YwEKgPNrswweXodAZi29',
  en: 'Xb7hH8MSUJpSbSDYk0k2',
  ar: 'aCChyB4P5WEomwRsOKRh',
  es: 'hpp4J3VqNfWAUOO0d1Us',
  de: 'Xb7hH8MSUJpSbSDYk0k2',
  zh: 'bhJUNIXWQQ94l8eI2VUf',
};

export function AppProvider({ children }) {
  const { lang } = useI18n();
  const [screen, setScreen] = useState('setup'); // 'setup' | 'main'
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('cogency_token') || '');
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('cogency_token'));
  const [mySocketId, setMySocketId] = useState(null);
  const [user, setUser] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [sessionStart, setSessionStart] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [trialExpired, setTrialExpired] = useState(false);
  const [appConfig, setAppConfig] = useState(null);
  const [connected, setConnected] = useState(false);

  const validateTour = async () => {
    if (!authToken) return;
    try {
      const res = await fetch(`${API_BASE}/api/validate-tour`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        setUser(prev => prev ? { ...prev, tourValidated: true } : prev);
      }
    } catch { /* ignore */ }
  };
  const [models, setModels] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [providers, setProviders] = useState({});
  const [providersReady, setProvidersReady] = useState(false);
  const [agents, setAgents] = useState([
    { persona: 'zai', model: 'zai:glm-5.1' },
    { persona: 'grok', model: 'xai:grok-4.20-0309-non-reasoning' }
  ]);
  const [synthModel, setSynthModel] = useState('deepseek:deepseek-r1');
  const [rounds, setRounds] = useState(2);
  const [debateMode, setDebateModeState] = useState(() => {
    try {
      const saved = localStorage.getItem('cogency_debate_mode');
      if (saved === 'sequential' || saved === 'parallel') return saved;
    } catch { /* ignore */ }
    return 'sequential';
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [collabMessages, setCollabMessages] = useState([]);
  const [customLogoImage, setCustomLogoImage] = useState(() => {
    try { return localStorage.getItem('cogency_chat_logo_image'); }
    catch { return null; }
  });
  const [showLogoUploader, setShowLogoUploader] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [collabOpen, setCollabOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showCharter, setShowCharter] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [showContactManager, setShowContactManager] = useState(false);
  const [setupError, setSetupError] = useState(null);
  const [setupLoading, setSetupLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [adminAlerts, setAdminAlerts] = useState([]);
  const [unreadAdminAlerts, setUnreadAdminAlerts] = useState(0);
  const [moderationPrompt, setModerationPrompt] = useState(null);
  const [interventionError, setInterventionError] = useState(null);
  const [moderationMode, setModerationModeState] = useState('present');
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState(() => localStorage.getItem('cogency_voice_id') || '');
  const [elevenLabsEnabled, setElevenLabsEnabled] = useState(false);
  const [selectedDomains, setSelectedDomains] = useState(() => {
    try {
      const saved = localStorage.getItem('cogency_domain');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && parsed !== 'free') return [parsed];
      }
    } catch {}
    return [];
  });
  const socketRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('cogency_domain', JSON.stringify(selectedDomains));
  }, [selectedDomains]);

  useEffect(() => {
    try { localStorage.setItem('cogency_debate_mode', debateMode); }
    catch { /* ignore */ }
  }, [debateMode]);

  useEffect(() => {
    if (isProcessing) {
      startThinkingSound();
    } else {
      stopThinkingSound();
    }
    return () => stopThinkingSound();
  }, [isProcessing]);

  useEffect(() => {
    const mapped = VOICE_BY_LANG[lang];
    if (mapped) {
      setVoiceId(mapped);
      localStorage.setItem('cogency_voice_id', mapped);
    }
  }, [lang]);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isSuperAdmin = user?.role === 'superadmin' || (user?.role === 'admin' && SUPER_ADMINS.includes(user?.email)) || !!user?.isSuperAdmin;
  const canConfigure = user?.role === 'admin' || user?.role === 'subscriber' || (user?.plan && user?.plan !== 'free');

  const fetchPendingCount = async () => {
    if (!authToken || !isSuperAdmin) return;
    try {
      const res = await fetch(`${API_BASE}/api/users`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setPendingCount(data.filter(u => u.role === 'pending').length);
      }
    } catch { /* ignore */ }
  };

  // ── Fetch user when authenticated ──
  useEffect(() => {
    if (!isAuthenticated || !authToken) return;
    fetch(`${API_BASE}/api/me`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.json())
      .then(data => {
        if (data.error === 'trial_expired') {
          setTrialExpired(true);
          setTrialStatus(data.trial || null);
          setUser(null);
          return;
        }
        if (data.user) {
          setUser(data.user);
          setTrialStatus(data.user.trial || null);
          setTrialExpired(false);
        }
      })
      .catch(() => {});
  }, [isAuthenticated, authToken]);

  // ── Fetch token usage when authenticated ──
  useEffect(() => {
    if (!isAuthenticated || !authToken) return;
    fetch(`${API_BASE}/api/me/usage`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.json())
      .then(data => {
        if (data && typeof data.usedTokens === 'number') {
          setTokenUsage(data);
        }
      })
      .catch(() => {});
  }, [isAuthenticated, authToken, user?.plan]);

  // ── Track session start when authenticated ──
  useEffect(() => {
    if (isAuthenticated && !sessionStart) {
      setSessionStart(Date.now());
    }
    if (!isAuthenticated) {
      setSessionStart(null);
    }
  }, [isAuthenticated, sessionStart]);

  useEffect(() => {
    // Voice list fetch disabled
    setElevenLabsEnabled(false);
    setVoices([]);
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/api/app-config`)
      .then(r => r.json())
      .then(data => setAppConfig(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (voiceId) localStorage.setItem('cogency_voice_id', voiceId);
  }, [voiceId]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, [authToken, user]);

  // ── Socket ──
  // Wait until the user has selected a skill before opening the realtime
  // connection. This avoids noisy WebSocket attempts while the skill selector
  // modal is still displayed on first login.
  useEffect(() => {
    if (!isAuthenticated || !authToken || !user?.skill) return;
    const socketUrl = API_BASE || window.location.origin;
    console.log('[AppContext] opening socket to', socketUrl);
    const socket = io(socketUrl, {
      auth: { token: authToken },
      transports: ['websocket', 'polling'],
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 60000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      setMySocketId(socket.id);
      const sid = new URLSearchParams(window.location.search).get('session');
      if (sid) joinSession(sid);
    });
    socket.on('disconnect', () => {
      console.log('[AppContext] socket disconnected');
      setConnected(false);
      setMySocketId(null);
      // Ne pas effacer moderationPrompt — attendre la reconnexion
    });
    socket.on('reconnect', () => {
      console.log('[AppContext] socket reconnected, re-joining session');
      setConnected(true);
      setMySocketId(socket.id);
      // Re-join the session if we were in one
      if (sessionId) {
        joinSession(sessionId);
      }
    });
    socket.on('connect_error', (err) => {
      console.warn('[AppContext] socket connect_error:', err.message);
      if (err.message === 'Authentication required') {
        localStorage.removeItem('cogency_token');
        setAuthToken('');
        setIsAuthenticated(false);
        setUser(null);
      }
    });
    socket.io.on('error', (err) => {
      console.warn('[AppContext] socket transport error:', err.message);
    });

    socket.on('session-created', (data) => {
      setSessionId(data.sessionId);
      setDocuments([]);
      updateUrlSession(data.sessionId);
    });

    socket.on('participant-joined', (data) => {
      setParticipants(prev => {
        if (prev.find(p => p.sid === data.socketId)) return prev;
        return [...prev, { sid: data.socketId, name: data.name, type: data.type }];
      });
    });

    socket.on('participant-left', (data) => {
      setParticipants(prev => prev.filter(p => p.sid !== data.socketId));
    });

    socket.on('collab-message', (data) => {
      setCollabMessages(prev => [...prev, data]);
      playNotificationSound();
    });

    socket.on('agent-invited', (data) => {
      const agent = data.agent;
      setAgents(prev => {
        if (prev.find(a => a.persona === agent.id)) return prev;
        return [...prev, { persona: agent.id, model: agent.model }];
      });
    });

    socket.on('status', (data) => {
      setMessages(prev => [...prev, { type: 'status', data }]);
      if (data.done) setIsProcessing(false);
    });

    socket.on('agent-response', (data) => {
      setMessages(prev => {
        const filtered = prev.filter(m => !(m.type === 'agent-stream' && m.data?.streamId === data.streamId));
        if (data.isError) {
          return [...filtered, { type: 'error', data: { content: data.content } }];
        }
        return [...filtered, { type: 'agent', data }];
      });
      if (!data.isError) playAnswerDing();
    });

    socket.on('agent-stream', (data) => {
      setMessages(prev => {
        const existingIndex = prev.findIndex(m => m.type === 'agent-stream' && m.data?.streamId === data.streamId);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = { type: 'agent-stream', data };
          return updated;
        }
        return [...prev, { type: 'agent-stream', data }];
      });
    });

    socket.on('round-complete', (data) => {
      setMessages(prev => [...prev, { type: 'round', data }]);
    });

    socket.on('final-answer', (data) => {
      setIsProcessing(false);
      setMessages(prev => [...prev, { type: 'final', data }]);
      playAnswerDing();
    });

    socket.on('error', (data) => {
      setIsProcessing(false);
      setMessages(prev => [...prev, { type: 'error', data: { content: data.message } }]);
    });

    socket.on('policy-warning', (data) => {
      setIsProcessing(false);
      setMessages(prev => [...prev, { type: 'error', data: { content: data.message } }]);
    });

    socket.on('policy-enforcement', (data) => {
      setIsProcessing(false);
      setMessages(prev => [...prev, { type: 'error', data: { content: data.message } }]);
      logout();
      setSetupError('Account suspended for policy violation. Contact administrator.');
    });

    socket.on('moderation-prompt', (data) => {
      setModerationPrompt({
        active: true,
        round: data.round,
        nextRound: data.nextRound,
        message: data.message,
        isExhaustionProposal: data.isExhaustionProposal || false
      });
      setMessages(prev => [...prev, { type: 'moderation', data }]);
    });

    socket.on('moderation-resolved', (data) => {
      setModerationPrompt(null);
    });

    socket.on('moderation-mode-changed', (data) => {
      setModerationModeState(data.mode || 'present');
    });

    socket.on('human-intervention', (data) => {
      setMessages(prev => [...prev, { type: 'human-intervention', data }]);
      playNotificationSound();
    });

    socket.on('intervention-error', (data) => {
      setMessages(prev => [...prev, { type: 'error', data: { content: data.message || 'Intervention failed. Please try again.' } }]);
      setInterventionError({ message: data.message, timestamp: Date.now() });
    });

    socket.on('collab-message-edited', (data) => {
      setCollabMessages(prev => prev.map(m => (m.messageId === data.messageId) ? data : m));
    });

    socket.on('collab-message-deleted', (data) => {
      setCollabMessages(prev => prev.filter(m => m.messageId !== data.messageId));
    });

    socket.on('human-intervention-edited', (data) => {
      setMessages(prev => prev.map(m => (m.type === 'human-intervention' && m.data?.interventionId === data.interventionId) ? { type: 'human-intervention', data } : m));
    });

    socket.on('human-intervention-deleted', (data) => {
      setMessages(prev => prev.filter(m => !(m.type === 'human-intervention' && m.data?.interventionId === data.interventionId)));
    });

    socket.on('debate-cancelled', (data) => {
      setIsProcessing(false);
      setMessages(prev => [...prev, { type: 'status', data: { message: `Debate cancelled by ${data.cancelledBy}.`, step: 'cancelled' } }]);
    });

    socket.on('admin-notification', (data) => {
      alert(`📢 ${data.title}\n\n${data.message}\n\n— ${data.from}`);
    });

    socket.on('admin-alert', (data) => {
      if (user?.role !== 'admin') return;
      setAdminAlerts(prev => [data, ...prev].slice(0, 100));
      setUnreadAdminAlerts(prev => prev + 1);
      playNotificationSound();
      // Show native web notification for critical types
      if (typeof window !== 'undefined' && window.Notification && Notification.permission === 'granted' && ['payment', 'security'].includes(data.type)) {
        try {
          new Notification(data.title || 'Cogency', {
            body: data.message || '',
            icon: '/logo-cogency.png',
            tag: data.type,
            requireInteraction: true,
            data: data.data
          });
        } catch {}
      }
    });

    socket.on('session-invite', (data) => {
      playNotificationSound();
      alert(`📨 Session Invitation\n\n${data.message}\n\nInvited by: ${data.inviter}`);
    });

    socket.on('document-attached', (data) => {
      setDocuments(prev => [...prev, { filename: data.filename, extractedText: data.extractedText }]);
    });

    return () => socket.disconnect();
  }, [isAuthenticated, authToken, user?.skill]);

  // ── Setup ──
  const checkSetup = useCallback(async () => {
    setSetupLoading(true);
    setSetupError(null);
    try {
      const res = await fetch(`${API_BASE}/api/health`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setProviders({ ollama: data.ollama, kimi: data.kimi, openrouter: data.openrouter, hf: data.hf, tchadgpt: data.tchadgpt, zai: data.zai, deepseek: data.deepseek, xai: data.xai, chatlyai: data.chatlyai, groq: data.groq, mistral: data.mistral, perplexity: data.perplexity, gemini: data.gemini, claude: data.claude });
      setModels(data.models || []);
      setProvidersReady(data.providersReady === true);
      setPersonas(data.personas || []);
      setSetupLoading(false);

      // All personas are returned (operational or not). Keep the selected persona,
      // but clear the model when the persona's provider is not available.
      const personaById = Object.fromEntries((data.personas || []).map(p => [p.id, p]));
      setAgents(prev => prev.map(a => {
        const persona = personaById[a.persona];
        if (!persona) return a;
        if (persona.available) return a;
        return { ...a, model: '' };
      }));

      // Validate default models exist in available models; fallback to same provider or first available.
      const availableIds = new Set((data.models || []).map(m => m.id));
      if (synthModel && !availableIds.has(synthModel)) {
        const fallbackSynth = data.models?.find(m => m.provider === 'deepseek') || data.models?.[0];
        if (fallbackSynth) setSynthModel(fallbackSynth.id);
      }
      setAgents(prev => prev.map(a => {
        if (!a.model) return a;
        if (availableIds.has(a.model)) return a;
        const persona = personaById[a.persona];
        if (persona?.available) {
          const fallback = data.models?.find(m => m.provider === persona.provider) || data.models?.[0];
          return fallback ? { ...a, model: fallback.id } : a;
        }
        return a;
      }));

      if (new URLSearchParams(window.location.search).get('session')) {
        setScreen('main');
      }
    } catch (err) {
      setSetupLoading(false);
      setSetupError('Cannot reach Cogency server. Make sure the server is running.');
    }
  }, [authToken]);

  const updateUrlSession = (id) => {
    const url = new URL(window.location.href);
    if (id) url.searchParams.set('session', id);
    else url.searchParams.delete('session');
    window.history.replaceState({}, '', url);
  };

  const setDebateMode = useCallback((mode) => {
    if (mode === 'sequential' || mode === 'parallel') {
      setDebateModeState(mode);
    }
  }, []);

  // ── Session ──
  const createSession = useCallback((cb) => {
    if (!socketRef.current) return;
    socketRef.current.emit('create-session', {
      question: '', rounds, agents, synthModel, ownerName: 'Host', debateMode
    }, (res) => {
      if (res.success) {
        setSessionId(res.sessionId);
        updateUrlSession(res.sessionId);
      }
      cb?.(res);
    });
  }, [rounds, agents, synthModel, debateMode]);

  const resumeSession = useCallback((sid, cb) => {
    if (!socketRef.current) return;
    socketRef.current.emit('resume-session', { sessionId: sid }, (res) => {
      if (res.success) {
        setSetupError(null);
      }
      cb?.(res);
    });
  }, []);

  const joinSession = useCallback((sid) => {
    if (!socketRef.current) return;
    socketRef.current.emit('join-session', { sessionId: sid, name: 'Guest', type: 'human' }, (res) => {
      if (res.success) {
        setSessionId(sid);
        setParticipants(res.participants || []);
        if (res.session) {
          setRounds(res.session.rounds);
          setAgents(res.session.agents.map(a => ({ persona: a.id, model: a.model })));
          setSynthModel(res.session.synthModel);
          setModerationModeState(res.session.moderationMode || 'present');
          setDebateModeState(res.session.debateMode || 'sequential');
          restoreMessages(res.session.messages);
          setCollabMessages(res.session.collabMessages || []);
          if (res.session.status === 'running') setIsProcessing(true);
          else if (res.session.status === 'complete') setIsProcessing(false);
          // Restore active moderation prompt only if a debate is actually running.
          // A prompt from a past session that is no longer running is stale.
          if (res.session.status === 'running' && res.session.messages && res.session.messages.length > 0) {
            const lastModIndex = [...res.session.messages].reverse().findIndex(m => m.type === 'moderation-prompt');
            if (lastModIndex >= 0) {
              const idx = res.session.messages.length - 1 - lastModIndex;
              const lastMod = res.session.messages[idx];
              const hasResolved = res.session.messages.slice(idx + 1).some(m =>
                (m.type === 'status' && (m.payload?.step === 'thinking' || m.payload?.step === 'cancelled' || m.payload?.step === 'synthesis')) ||
                m.type === 'agent-response' ||
                m.type === 'round-complete' ||
                m.type === 'final-answer' ||
                m.type === 'error'
              );
              if (!hasResolved) {
                setModerationPrompt({
                  active: true,
                  round: lastMod.payload?.round,
                  nextRound: lastMod.payload?.nextRound,
                  message: lastMod.payload?.message
                });
              }
            }
          }
        }
      } else {
        setSetupError(res.error || 'Failed to join session');
        updateUrlSession(null);
      }
    });
  }, []);

  const saveSession = useCallback(() => {
    if (sessionId) {
      navigator.clipboard.writeText(window.location.href);
      return;
    }
    createSession((res) => {
      if (res?.success) navigator.clipboard.writeText(window.location.href);
    });
  }, [sessionId, createSession]);

  const handleLogoUpload = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      try { localStorage.setItem('cogency_chat_logo_image', dataUrl); } catch {}
      setCustomLogoImage(dataUrl);
      setShowLogoUploader(false);
    };
    reader.readAsDataURL(file);
  }, []);

  const resetLogo = useCallback(() => {
    try { localStorage.removeItem('cogency_chat_logo_image'); } catch {}
    setCustomLogoImage(null);
    setShowLogoUploader(false);
  }, []);

  const startNewSession = useCallback(() => {
    setMessages([]);
    setCollabMessages([]);
    setIsProcessing(false);
    setModerationPrompt(null);
    setSessionId(null);
    setParticipants([]);
    setModerationModeState('present');
    updateUrlSession(null);
    setScreen('main');
    createSession((res) => {
      if (res?.success) {
        setSessionId(res.sessionId);
        updateUrlSession(res.sessionId);
      }
    });
  }, [createSession, setScreen]);

  const persistCurrentSession = useCallback(async () => {
    if (!sessionId) return;
    try {
      await fetch(`${API_BASE}/api/sessions/${sessionId}/persist`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` }
      });
    } catch (e) {
      console.error('Failed to persist session:', e);
    }
  }, [sessionId, authToken]);

  const closeSession = useCallback(async () => {
    // Leave the session room on the server so stale moderation events stop
    if (socketRef.current && sessionId) {
      socketRef.current.emit('leave-session', { sessionId });
    }
    await persistCurrentSession();
    setSessionId(null);
    setMessages([]);
    setCollabMessages([]);
    setIsProcessing(false);
    setModerationPrompt(null);
    setParticipants([]);
    updateUrlSession(null);
    localStorage.removeItem('cogency_session_id');
    localStorage.removeItem('cogency_moderation_mode');
    setScreen('setup');
  }, [persistCurrentSession, updateUrlSession, setScreen, sessionId]);

  const setModerationMode = useCallback((mode) => {
    if (!socketRef.current || !sessionId) return;
    socketRef.current.emit('set-moderation-mode', { sessionId, mode });
    setModerationModeState(mode);
  }, [sessionId]);

  const sendChat = useCallback((text) => {
    if (!socketRef.current || !sessionId || !text.trim()) return;
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    socketRef.current.emit('chat-message', { sessionId, text, messageId });
    return messageId;
  }, [sessionId]);

  const sendIntervention = useCallback((text) => {
    if (!socketRef.current || !sessionId || !socketRef.current.connected) {
      console.warn('[AppContext] sendIntervention blocked — socket:', !!socketRef.current, 'connected:', socketRef.current?.connected, 'sessionId:', !!sessionId);
      return null;
    }
    const interventionId = `int_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    socketRef.current.emit('human-intervention', { sessionId, text, skip: false, interventionId });
    return interventionId;
  }, [sessionId]);

  const skipIntervention = useCallback(() => {
    if (!socketRef.current || !sessionId || !socketRef.current.connected) return;
    socketRef.current.emit('human-intervention', { sessionId, text: '', skip: true });
  }, [sessionId]);

  const sendTypingIntervention = useCallback(() => {
    if (!socketRef.current || !sessionId) return;
    socketRef.current.emit('typing-intervention', { sessionId });
  }, [sessionId]);

  const cancelDebate = useCallback(() => {
    if (!socketRef.current || !sessionId) return;
    socketRef.current.emit('cancel-debate', { sessionId });
  }, [sessionId]);

  const editChatMessage = useCallback((messageId, text) => {
    if (!socketRef.current || !sessionId || !messageId) return;
    socketRef.current.emit('edit-chat-message', { sessionId, messageId, text });
  }, [sessionId]);

  const deleteChatMessage = useCallback((messageId) => {
    if (!socketRef.current || !sessionId || !messageId) return;
    socketRef.current.emit('delete-chat-message', { sessionId, messageId });
  }, [sessionId]);

  const editHumanIntervention = useCallback((interventionId, text) => {
    if (!socketRef.current || !sessionId || !interventionId) return;
    socketRef.current.emit('edit-human-intervention', { sessionId, interventionId, text });
  }, [sessionId]);

  const deleteHumanIntervention = useCallback((interventionId) => {
    if (!socketRef.current || !sessionId || !interventionId) return;
    socketRef.current.emit('delete-human-intervention', { sessionId, interventionId });
  }, [sessionId]);

  const inviteUserToSession = useCallback((userId, cb) => {
    if (!socketRef.current || !sessionId) return;
    socketRef.current.emit('invite-user-to-session', { sessionId, userId }, (res) => {
      cb?.(res);
    });
  }, [sessionId]);

  const inviteAgent = useCallback((personaId, modelId) => {
    if (!socketRef.current || !sessionId) return;
    const persona = personas.find(p => p.id === personaId);
    if (!persona) return;
    const agent = {
      id: persona.id, name: persona.name, model: modelId,
      persona: persona.persona, color: persona.color, icon: persona.icon
    };
    socketRef.current.emit('invite-agent', { sessionId, agent });
    setAgents(prev => {
      if (prev.find(a => a.persona === personaId)) return prev;
      return [...prev, { persona: personaId, model: modelId }];
    });
  }, [sessionId, personas]);

  // ── Debate ──
  const sendQuestion = useCallback((question) => {
    if (!socketRef.current || !question.trim() || isProcessing) return;
    setIsProcessing(true);
    setMessages(prev => [...prev, { type: 'user', data: { content: question } }]);

    const agentList = agents.map((a, i) => {
      const p = personas.find(p => p.id === a.persona);
      return {
        id: p?.id || `agent${i}`, name: p?.name || `Agent ${i + 1}`,
        model: a.model, persona: p?.persona || '',
        color: p?.color || '#3b82f6', icon: p?.icon || '🔬'
      };
    });

    socketRef.current.emit('ask', {
      question: question.trim(), rounds, agents: agentList,
      synthModel: synthModel || 'ollama:qwen2.5:7b', sessionId,
      domains: selectedDomains, debateMode
    });

    if (!sessionId) {
      createSession();
    }
  }, [isProcessing, agents, personas, rounds, synthModel, sessionId, createSession, debateMode]);

  const uploadDocument = useCallback(async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('document', file);
    try {
      const res = await fetch(`${API_BASE}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('cogency_token')}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setDocuments(prev => [...prev, { filename: data.filename, extractedText: data.extractedText }]);
        if (sessionId && socketRef.current) {
          socketRef.current.emit('attach-document', {
            sessionId,
            filename: data.filename,
            extractedText: data.extractedText
          });
        }
        return data;
      }
    } catch (err) {
      console.error('Upload error:', err);
    }
    return null;
  }, [sessionId]);

  // ── Helpers ──
  const restoreMessages = (sessionMessages) => {
    if (!sessionMessages) return;
    const restored = sessionMessages.map(m => {
      switch (m.type) {
        case 'status': return { type: 'status', data: m.payload };
        case 'agent-response':
        case 'agent': return m.payload?.isError ? { type: 'error', data: { content: m.payload.content } } : { type: 'agent', data: m.payload };
        case 'user': return { type: 'user', data: { content: m.payload?.content || m.payload } };
        case 'round-complete': return { type: 'round', data: m.payload };
        case 'final-answer': return { type: 'final', data: m.payload };
        case 'chat-message': return { type: 'chat', data: m.payload };
        case 'moderation-prompt': return { type: 'moderation', data: m.payload };
        case 'human-intervention': return { type: 'human-intervention', data: m.payload };
        default: return null;
      }
    }).filter(Boolean);
    setMessages(restored);
  };

  const addAgent = () => {
    if (agents.length >= 10) return;
    const used = new Set(agents.map(a => a.persona));
    const unusedPersonas = personas.filter(p => !used.has(p.id));
    const persona = unusedPersonas.find(p => p.available) || unusedPersonas[0] || personas.find(p => !used.has(p.id)) || personas[0];
    const personaId = persona?.id || '';
    let model = '';
    if (persona?.available && persona.provider) {
      model = models.find(m => m.provider === persona.provider)?.id || models[0]?.id || '';
    }
    setAgents(prev => [...prev, { persona: personaId, model }]);
  };

  const removeAgent = (index) => {
    if (agents.length <= 2) return;
    setAgents(prev => prev.filter((_, i) => i !== index));
  };

  const updateAgentPersona = (index, personaId) => {
    setAgents(prev => prev.map((a, i) => {
      if (i !== index) return a;
      const persona = personas.find(p => p.id === personaId);
      if (!persona?.available) return { ...a, persona: personaId, model: '' };
      const model = models.find(m => m.provider === persona.provider)?.id || models[0]?.id || '';
      return { ...a, persona: personaId, model };
    }));
  };

  const updateAgentModel = (index, modelId) => {
    setAgents(prev => prev.map((a, i) => i === index ? { ...a, model: modelId } : a));
  };

  const validateKey = async (provider, key) => {
    try {
      const res = await fetch(`${API_BASE}/api/validate-key`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ provider, key })
      });
      const data = await res.json();
      if (data.valid) checkSetup();
      return data.valid;
    } catch { return false; }
  };

  const login = async (token) => {
    setAuthToken(token);
    setIsAuthenticated(true);
    try {
      const res = await fetch(`${API_BASE}/api/me`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.error === 'trial_expired') {
        setTrialExpired(true);
        setTrialStatus(data.trial || null);
        setUser(null);
        return;
      }
      if (data.user) {
        setUser(data.user);
        setTrialStatus(data.user.trial || null);
        setTrialExpired(false);
        // Trigger service worker push registration for admin users
        if (data.user.role === 'admin' && typeof window !== 'undefined') {
          try { window.dispatchEvent(new CustomEvent('cogency-login')); } catch {}
        }
      }
    } catch { /* ignore */ }
  };

  const clearAdminAlerts = useCallback(() => {
    setAdminAlerts([]);
    setUnreadAdminAlerts(0);
  }, []);

  const updateUserSkill = async (skill) => {
    if (!authToken) return false;
    try {
      const res = await fetch(`${API_BASE}/api/users/me/skill`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({ skill })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setUser(prev => prev ? { ...prev, skill } : prev);
        return true;
      }
    } catch { /* ignore */ }
    return false;
  };

  const logout = async () => {
    await persistCurrentSession();
    if (socketRef.current) socketRef.current.disconnect();
    // Purge cache / localStorage (preserve language and theme preferences)
    const lang = localStorage.getItem('cogency_lang');
    const theme = localStorage.getItem('cogency_theme');
    localStorage.clear();
    if (lang) localStorage.setItem('cogency_lang', lang);
    if (theme) localStorage.setItem('cogency_theme', theme);
    setAuthToken('');
    setIsAuthenticated(false);
    setUser(null);
    setPendingCount(0);
    setAdminAlerts([]);
    setUnreadAdminAlerts(0);
    window.location.href = '/';
  };

  // Voice output disabled
  const speak = useCallback(async (text, options = {}) => {
    // TTS disabled
  }, []);

  const stopSpeaking = useCallback(() => {
    stopAllAudio();
  }, []);

  return (
    <AppContext.Provider value={{
      screen, setScreen,
      authToken, isAuthenticated, user, isAdmin, isSuperAdmin, canConfigure, login, logout,
      updateUserSkill,
      tokenUsage, sessionStart,
      trialStatus, trialExpired, validateTour,
      appConfig,
      connected,
      models, personas, providers, providersReady,
      agents, setAgents, synthModel, setSynthModel, rounds, setRounds, debateMode, setDebateMode,
      isProcessing, messages, setMessages, collabMessages,
      sessionId, participants,
      customLogoImage, showLogoUploader, setShowLogoUploader, handleLogoUpload, resetLogo,
      collabOpen, setCollabOpen,
      showHelp, setShowHelp,
      showInvite, setShowInvite,
      showPricing, setShowPricing,
      selectedDomains, setSelectedDomains,
      showCharter, setShowCharter,
        showContact, setShowContact,
      showThemeSelector, setShowThemeSelector,
      showContactManager, setShowContactManager,
      setupError, setSetupError,
      setupLoading,
      pendingCount,
      fetchPendingCount,
      adminAlerts, unreadAdminAlerts, clearAdminAlerts,
      socket: socketRef.current, mySocketId,
      checkSetup,
      createSession, joinSession, resumeSession, saveSession, startNewSession, closeSession, persistCurrentSession, sendChat, cancelDebate, editChatMessage, deleteChatMessage, editHumanIntervention, deleteHumanIntervention, inviteAgent, inviteUserToSession,
      sendQuestion,
      documents, uploadDocument,
      addAgent, removeAgent, updateAgentPersona, updateAgentModel,
      validateKey,
      moderationPrompt, moderationMode, setModerationMode, sendIntervention, skipIntervention, sendTypingIntervention, interventionError, setInterventionError,
      speak, stopSpeaking, voices, voiceId, setVoiceId,
    }}>
      {children}
    </AppContext.Provider>
  );
}
